import { BookConsultationCard } from '@/features/programs/shared/ui/BookConsultationCard';
import {
    ArticleDto,
    NewbornProgram,
    ProductCardViewModel,
} from '@/features/programs/model/types';
import { CaregiverRoleEnum } from '@/shared/enums';
import { getTimedGreeting } from '@/shared/timedGreeting';
import { ProgressDisplay } from '@/features/programs/shared/ui/ProgressDisplay';
import { EditIcon } from '@/components/Icons';
import { EditDueDateModal } from './ui/EditDueDateModal';
import { HaveYouGivenBirthModal } from './ui/HaveYouGivenBirthModal';
import { Button, BackButton, VerticalContainer } from '@/components';
import { R as NewbornResources } from './resources/NewbornResources.resx';
import { R as ProgramResources } from '@/features/programs/resources/ProgramsResources.resx';
import { CongratulationOnYourChildModal } from './ui/CongratulationOnYourChildModal';
import { ArticlesGrid } from '@/features/programs/shared/ui/ArticleGrid';
import { useNewbornProgram } from '@/features/programs/widgets/newborn/lib/useNewbornProgram';
import { CaregiversGrid } from '@/features/programs/shared/ui/CaregiversGrid';
import { NewbornInfoSection } from '@/features/programs/widgets/newborn/ui/NewbornInfoSection';

export interface NewBornProgramProps {
    productCardViewModels: ProductCardViewModel[];
    articles?: ArticleDto[];
    newbornProgram: NewbornProgram;
}

export const NewBornProgram = ({
    productCardViewModels,
    articles,
    newbornProgram: initialNewbornProgram,
}: NewBornProgramProps) => {
    const {
        newbornProgram,
        isPending,
        showEditDueDateModal,
        showHaveYouGivenBirthModal,
        showCongratulationOnYourChildModal,
        openEditDueDateModal,
        closeEditDueDateModal,
        openHaveYouGivenBirthModal,
        closeHaveYouGivenBirthModal,
        closeCongratulationOnYourChildModal,
        handleBabyIsBorn,
    } = useNewbornProgram(initialNewbornProgram);

    const leftProgressDisplay = (
        <>
            <div className="invisible text-sm">spacer</div>
            <div className="text-text-heading text-3xl font-bold sm:text-[3rem] sm:leading-12">
                {newbornProgram.Labels.TrimesterOrdinal}
            </div>
            <div className="text-sm">{newbornProgram.Labels.Trimester}</div>
        </>
    );

    const centerProgressDisplay = (
        <>
            <div className="text-sm">{newbornProgram.Labels.Week}</div>
            <div className="text-text-heading text-3xl font-bold sm:text-[3rem] sm:leading-12">
                {newbornProgram.PregnancyWeek}
            </div>
            {newbornProgram.Labels.PregnancyDayRemainderAfterWeeks && (
                <div className="text-sm">
                    {newbornProgram.Labels.PregnancyDayRemainderAfterWeeks}
                </div>
            )}
        </>
    );

    const rightProgressDisplay = (
        <>
            <div className="invisible text-sm">spacer</div>
            <div className="text-text-heading text-3xl font-bold sm:text-[3rem] sm:leading-12">
                {newbornProgram.DaysUntilDueDate}
            </div>
            <div className="text-sm">
                {newbornProgram.Labels.DaysUntilExpiration}
            </div>
        </>
    );

    const footerProgressDisplay = (
        <div className="flex w-full flex-col items-center gap-5 lg:flex-row lg:justify-between">
            <div className="flex items-center gap-2">
                <span>{newbornProgram.Labels.DueDate}</span>
                <div onClick={openEditDueDateModal}>
                    <EditIcon className="cursor-pointer" />
                </div>
            </div>
            {newbornProgram.PregnancyWeek >= 29 && (
                <div>
                    <Button
                        variant="secondary"
                        outline
                        small
                        onClick={openHaveYouGivenBirthModal}
                        disabled={showHaveYouGivenBirthModal}
                    >
                        {NewbornResources.BabyIsBornButtonText}
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <VerticalContainer spacing="none" className="animate-fade-in">
            <div className="content-grid-full bg-action-orange-extralight relative z-0 w-full overflow-hidden sm:pt-8">
                <div className="flex-justify-center flex w-full flex-col items-center gap-6 px-4 md:px-10 lg:h-80 lg:flex-row lg:items-end">
                    <div className="h-full w-full lg:w-[calc(50%+3rem)] lg:pt-2">
                        <div className="w-full flex-col items-end justify-center gap-4 lg:ml-auto lg:h-80 lg:max-w-[660px] lg:justify-start">
                            <BackButton className="sm:hidden" />
                            <h4 className="text-action-orange mt-4 mb-2 px-4 text-center text-[18px] lg:text-left">
                                {newbornProgram.Labels.Title}
                            </h4>
                            <h2 className="px-4 text-center text-[32px] lg:text-left">
                                {getTimedGreeting(newbornProgram.ProfileName)}
                            </h2>
                            {!newbornProgram.IsBabyBorn && (
                                <ProgressDisplay
                                    left={leftProgressDisplay}
                                    center={centerProgressDisplay}
                                    right={rightProgressDisplay}
                                    footer={footerProgressDisplay}
                                    progress={newbornProgram.PregnancyProgress}
                                />
                            )}
                        </div>
                    </div>

                    {/* Main Image */}
                    <div className="z-0 flex w-full justify-center lg:ml-[100px] lg:w-[calc(50%-3rem)] lg:justify-start">
                        <img
                            className="-mb-5 h-auto max-w-full"
                            src={newbornProgram.NewbornMainImagePath}
                            width={300}
                            height={300}
                            alt="main-illustration"
                            aria-hidden={true}
                        />
                    </div>
                </div>
                <img
                    className="bottom-0 left-0 z-0 -mb-px h-[50px] max-h-[88px] w-full md:h-[88px]"
                    src="/images/icons_shape/hero-background-mask.svg"
                    alt=""
                />
            </div>

            <div className="z-20 mx-auto -mt-[30px] grid max-w-[1440px] grid-cols-1 justify-center gap-4 px-4 pb-6 md:-mt-[68px] md:px-10 md:pb-10 lg:grid-cols-3">
                {productCardViewModels.map((vm: ProductCardViewModel) => (
                    <div
                        key={`${CaregiverRoleEnum[vm.CardDto.Role]}`}
                        className="flex basis-full justify-center md:basis-[calc(50%-0.5rem)]"
                    >
                        <BookConsultationCard
                            productCard={vm}
                            className="z-20 mb-8 h-full w-full md:min-h-[236px] md:max-w-[540px] lg:max-w-[720px]"
                            showTags={true}
                        />
                    </div>
                ))}
            </div>

            <VerticalContainer spacing="none">
                <VerticalContainer
                    spacing="sm"
                    className="bg-secondary-blue-extralight items-center"
                >
                    <NewbornInfoSection newbornProgram={newbornProgram} />
                </VerticalContainer>

                <VerticalContainer className="bg-bg-surface py-10">
                    <VerticalContainer
                        spacing="xl"
                        className="mx-auto w-screen max-w-[1440px] px-4 md:px-10"
                    >
                        <h2>{ProgramResources.MeetTheTeamHeader}</h2>
                        <CaregiversGrid
                            caregivers={newbornProgram.Caregivers}
                            backgroundColor="var(--color-bg-surface)"
                        />
                    </VerticalContainer>
                </VerticalContainer>

                <VerticalContainer className="bg-bg-subtle py-10">
                    <VerticalContainer
                        spacing="xl"
                        className="mx-auto w-screen max-w-[1440px] px-4 md:px-10"
                    >
                        <h2>{ProgramResources.ArticlesHeader}</h2>
                        <ArticlesGrid
                            articles={articles}
                            backgroundColor="var(--color-bg-subtle)"
                        />
                    </VerticalContainer>
                </VerticalContainer>
            </VerticalContainer>

            {showEditDueDateModal && (
                <EditDueDateModal
                    dueDate={new Date(newbornProgram.DateOfBirth)}
                    onClose={closeEditDueDateModal}
                />
            )}
            {showHaveYouGivenBirthModal && (
                <HaveYouGivenBirthModal
                    onClose={closeHaveYouGivenBirthModal}
                    onStillPregnant={closeHaveYouGivenBirthModal}
                    onBabyIsBorn={handleBabyIsBorn}
                    isPending={isPending}
                />
            )}
            {showCongratulationOnYourChildModal && (
                <CongratulationOnYourChildModal
                    onClose={closeCongratulationOnYourChildModal}
                />
            )}
        </VerticalContainer>
    );
};
